import { FC, useEffect, useMemo, useState } from 'react';
import {
    Tokens,
    MfaProviderIntegrationConfig,
    isTokenKey,
    ExternalMfaProviderConfigValues,
    ExternalMfaProviderSettings,
    MfaProviderIntegrationId,
    isMfaProviderIntegrationId,
    TokenKey
} from './types';
import TokenDropdown from './components/TokenDropdown';
import MFATokenProvider from './MFATokenProvider';
import ExternalMfaConfigRenderer from './components/ExternalMfaConfigRenderer';
import { TokenRow } from './components/TokenTable';

interface Props {
    listOptions: {
        title: string;
        value: TokenKey;
        disabled: boolean;
        subtitle: string;
        isExternal: boolean;
    }[];
    save: () => void;
    dispatch: React.Dispatch<any>;
    visibleFields: Tokens;
    editToken?: TokenKey;
    editTenantId?: string;
    editEntraIdToken?: string;
    onCancel: () => void;
    selectedMfaToken?: TokenKey;
    setSelectedMfaToken: (token: TokenKey) => void;
    configuredTokens: TokenRow[];
    externalConfigs: MfaProviderIntegrationConfig[];
    onExternalConfigSave: (integrationId: MfaProviderIntegrationId, data: ExternalMfaProviderConfigValues) => void;
    alreadyConfiguredExternalMFAProviders: ExternalMfaProviderSettings;
}

const MFATokenLayout: FC<Props> = ({
    selectedMfaToken,
    setSelectedMfaToken,
    onCancel,
    listOptions,
    save,
    visibleFields,
    dispatch,
    editToken,
    editTenantId,
    editEntraIdToken,
    configuredTokens,
    externalConfigs,
    onExternalConfigSave,
    alreadyConfiguredExternalMFAProviders
}) => {
    const [isDisabled, setIsDisabled] = useState(false);

    const dropdownOptions = useMemo(() => {
        return listOptions.map(({ isExternal, ...option }) => option);
    }, [listOptions]);

    const selectedOption = useMemo(() => {
        return listOptions.find((option) => option.value === selectedMfaToken);
    }, [listOptions, selectedMfaToken]);

    useEffect(() => {
        if (editToken) {
            setSelectedMfaToken(editToken);
            setIsDisabled(true);
        }
    }, [editToken, setSelectedMfaToken]);

    const isExternalConfig = !!selectedOption && selectedOption.isExternal;

    const externalConfigId =
        isExternalConfig && !!selectedMfaToken && isMfaProviderIntegrationId(selectedMfaToken)
            ? selectedMfaToken
            : undefined;
    const currentExternalConfig = externalConfigId
        ? externalConfigs?.find((config) => config.id === externalConfigId)
        : undefined;
    const savedExternalValues = currentExternalConfig
        ? alreadyConfiguredExternalMFAProviders[currentExternalConfig.id]?.values
        : undefined;

    return (
        <div className="bg-sf-white font-sf flex flex-col gap-4 relative">
            <div className="p-6 flex flex-col rounded-md border min-h-[284px] gap-4">
                <TokenDropdown
                    label="MFA provider"
                    placeholder="Select IdP"
                    listOptions={dropdownOptions}
                    value={selectedMfaToken}
                    onChange={setSelectedMfaToken}
                    isDisabled={isDisabled}
                />
                <div className="flex flex-1 flex-col">
                    <div className="flex flex-1 flex-col gap-4">
                        {isExternalConfig && currentExternalConfig && externalConfigId ? (
                            <ExternalMfaConfigRenderer
                                config={currentExternalConfig}
                                onSave={(data) => onExternalConfigSave(externalConfigId, data)}
                                onCancel={onCancel}
                                initialData={savedExternalValues}
                            />
                        ) : isTokenKey(selectedMfaToken) ? (
                            <MFATokenProvider
                                editToken={editToken}
                                editTenantId={editTenantId}
                                editEntraIdToken={editEntraIdToken}
                                selectedMfaToken={selectedMfaToken}
                                visibleFields={visibleFields}
                                dispatch={dispatch}
                                save={save}
                                onCancel={onCancel}
                                configuredTokens={configuredTokens}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MFATokenLayout;
